import { PayloadSchemaProperty, TelemetryEntry, TmState, ValidationRule } from "./TestCaseTypes";

type CatalogMatchStatus = "Matched" | "Mismatched" | "Unknown";

export interface ValidationConfigRule {
  isValidate: boolean;
  type?: "constant" | "range" | "enumMatch" | "equalto";
  value?: number | string;
  min?: number | string;
  max?: number | string;
}

export interface ValidationConfig {
  validationConfigId: string;
  validationName: string;
  commandId: number;
  commandName: string;
  saId: number;
  daId: number;
  mode: number | null;
  subsystem: string;
  description: string;
  version: string;
  tmCatalogVersion: string;
  type: "Default" | "Custom";
  catalogMatchStatus: CatalogMatchStatus;
  validations?: {
    payload?: Record<string, ValidationConfigRule>;
  };
  tmPayloadSchema?: {
    type?: "object";
    properties?: Record<string, PayloadSchemaProperty>;
  };
}

type ValidationConfigTelemetryEntry = TelemetryEntry & {
  validationConfig?: ValidationConfig;
};


// Matched  → use the stored validation rules (has isValidate, type, min/max/value)
// Mismatched/Unknown → fall back to the raw TM schema properties for display
export const getPayloadSource = (
  validationConfig: ValidationConfig
): Record<string, ValidationConfigRule | PayloadSchemaProperty> => {
  if (validationConfig.catalogMatchStatus === "Matched") {
    return validationConfig.validations?.payload || {};
  }
  return validationConfig.tmPayloadSchema?.properties || {};
};

const getPropertyTypeFromRule = (rule: ValidationConfigRule | null | undefined): PayloadSchemaProperty["type"] => {
  if (!rule) return "string";

  try {
    const numericValue = rule.value;
    if (typeof numericValue === "number") {
      return Number.isInteger(numericValue) ? "integer" : "number";
    }
    if (typeof rule.min === "number" || typeof rule.max === "number") {
      const minIsInteger = rule.min == null || Number.isInteger(rule.min);
      const maxIsInteger = rule.max == null || Number.isInteger(rule.max);
      return minIsInteger && maxIsInteger ? "integer" : "number";
    }
    return "string";
  } catch {
    return "string";
  }
};

const getPayloadSchemaProperties = (validationConfig: ValidationConfig): Record<string, PayloadSchemaProperty> => {
  const payloadSource = getPayloadSource(validationConfig);

  if (validationConfig.catalogMatchStatus === "Mismatched" || validationConfig.catalogMatchStatus === "Unknown") {
    const properties = payloadSource as Record<string, PayloadSchemaProperty>;
    if (!properties || typeof properties !== "object") {
      console.warn("Mismatched or Unknown config has invalid schema properties:", validationConfig.validationConfigId);
      return {};
    }
    return properties;
  }

  try {
    return Object.entries(payloadSource as Record<string, ValidationConfigRule>).reduce<Record<string, PayloadSchemaProperty>>((acc, [key, rule]) => {
      const schemaProperty = validationConfig.tmPayloadSchema?.properties?.[key];
      const inferredType = getPropertyTypeFromRule(rule);

      acc[key] = {
        ...schemaProperty,
        type: schemaProperty?.type ?? inferredType,
      };
      return acc;
    }, {});
  } catch (error) {
    console.error("Error building payload schema properties:", error);
    return {};
  }
};

const getPayloadTemplate = (validationConfig: ValidationConfig): Record<string, unknown> => {
  try {
    const payloadSource = getPayloadSource(validationConfig);
    if (!payloadSource || typeof payloadSource !== "object") {
      console.warn("Payload source is not an object:", validationConfig.validationConfigId);
      return {};
    }
    return Object.keys(payloadSource).reduce<Record<string, unknown>>((acc, key) => {
      const rule = (payloadSource as Record<string, any>)[key];
      // Extract value from validation rule - use min for range type, value for others
      if (rule?.value !== undefined && rule?.value !== null && rule?.value !== "") {
        acc[key] = rule.value;
      } else if (rule?.min !== undefined && rule?.min !== null) {
        acc[key] = rule.min;
      } else if (rule?.max !== undefined && rule?.max !== null) {
        acc[key] = rule.max;
      } else {
        acc[key] = "";
      }
      return acc;
    }, {});
  } catch (error) {
    console.error("Error building payload template:", error);
    return {};
  }
};

// Returns null (never throws) so callers can safely .map().filter(Boolean).
// subsystem is allowed to be empty — some commands have no subsystem in the catalog.
export const toTelemetryEntry = (validationConfig: ValidationConfig): ValidationConfigTelemetryEntry | null => {
  try {
    if (
      !validationConfig.commandId ||
      !validationConfig.commandName ||
      validationConfig.saId == null ||
      validationConfig.daId == null
    ) {
      console.warn("ValidationConfig has missing critical fields:", validationConfig.validationConfigId);
      return null;
    }

    return {
      commandId: validationConfig.commandId,
      commandName: validationConfig.commandName,
      saId: validationConfig.saId,
      daId: validationConfig.daId,
      subsystem: validationConfig.subsystem,
      payload: getPayloadTemplate(validationConfig),
      payloadSchema: {
        type: "object",
        properties: getPayloadSchemaProperties(validationConfig),
      },
      mode: validationConfig.mode,
      validationConfig,
    };
  } catch (error) {
    console.error("Failed to convert ValidationConfig to TelemetryEntry:", error, validationConfig.validationConfigId);
    return null;
  }
};

const toValidationRule = (rule: ValidationConfigRule): ValidationRule => {
  try {
    if (rule.type === "range" || rule.type === "enumMatch") {
      const min = rule.min != null ? String(rule.min) : "0";
      const max = rule.max != null ? String(rule.max) : "0";
      return { type: rule.type, min, max, value: "0" };
    }
    return {
      type: rule.type === "equalto" ? "equal" : "constant",
      min: "0",
      max: "0",
      value: rule.value != null ? String(rule.value) : "0",
    };
  } catch (error) {
    console.error("Error converting validation rule:", error, rule);
    return { type: "constant", min: "0", max: "0", value: "0" };
  }
};

// Leaf = has BOTH `isValidate` and `type`. Group nodes (Sys_time_config etc.)
// carry a sibling `isValidate` rollup too, but never their own `type` —
// that's the distinguishing signal.
const isLeafValidationRule = (node: unknown): node is ValidationConfigRule => {
  return (
    !!node &&
    typeof node === "object" &&
    !Array.isArray(node) &&
    "isValidate" in (node as object) &&
    "type" in (node as object)
  );
};

// Recurses through the nested payload tree, collecting every leaf with
// isValidate:true. Deliberately ignores a group's own isValidate rollup —
// that flag summarizes the group, it doesn't gate its children.
const collectCheckedValidations = (
  node: unknown,
  prefix: string,
  checkedParams: string[],
  validations: Record<string, ValidationRule>
): void => {
  if (node == null || typeof node !== "object") return;

  if (isLeafValidationRule(node)) {
    if (node.isValidate) {
      checkedParams.push(prefix);
      validations[prefix] = toValidationRule(node);
    }
    return;
  }

  if (Array.isArray(node)) {
    node.forEach((item, idx) => {
      collectCheckedValidations(item, `${prefix}[${idx}]`, checkedParams, validations);
    });
    return;
  }

  Object.entries(node as Record<string, unknown>).forEach(([key, value]) => {
    if (key === "isValidate") return;
    // Format numeric object keys as array indices (e.g. Data[0] instead of Data.0)
    const isNumeric = /^\d+$/.test(key);
    const childPath = isNumeric
      ? (prefix ? `${prefix}[${key}]` : `[${key}]`)
      : (prefix ? `${prefix}.${key}` : key);
    collectCheckedValidations(value, childPath, checkedParams, validations);
  });
};
// Extracts pre-filled validation state from a ValidationConfig-backed TelemetryEntry.
// Only applies to Matched configs — Mismatched/Unknown have no reliable pre-defined rules.
// Returns null if the entry has no attached validationConfig or no checked params.
export const getInitialTmStateFromValidationConfig = (
  telemetryEntry: unknown
): TmState | null => {
  try {
    const validationConfig = (telemetryEntry as ValidationConfigTelemetryEntry | undefined)?.validationConfig;
    if (!validationConfig || validationConfig.catalogMatchStatus !== "Matched") return null;

    const checkedParams: string[] = [];
    const validations: Record<string, ValidationRule> = {};

    const payload = validationConfig.validations?.payload;
    if (!payload || typeof payload !== "object") {
      console.warn("Validation config has invalid payload:", validationConfig.validationConfigId);
      return null;
    }

    Object.entries(payload).forEach(([key, rule]) => {
      try {
        if (!rule || !rule.isValidate) return;
        checkedParams.push(key);
        validations[key] = toValidationRule(rule);
      } catch (err) {
        console.warn("Failed to process validation rule for key:", key, err);
      }
    });
    collectCheckedValidations(payload, "", checkedParams, validations);
    return { checkedParams, validations };
  } catch (error) {
    console.error("Error extracting initial state from validation config:", error);
    return null;
  }
};


// Guards against malformed entries in the API response.
// subsystem is intentionally not required — some catalog entries have an empty subsystem.
// validations and tmPayloadSchema are optional fields; only their type is checked if present.
const isValidValidationConfig = (config: unknown): config is ValidationConfig => {
  if (!config || typeof config !== "object") return false;
  const c = config as Record<string, unknown>;

  if (typeof c.validationConfigId !== "string" || !c.validationConfigId) return false;
  if (typeof c.commandId !== "number" || !c.commandId) return false;
  if (typeof c.saId !== "number") return false;
  if (typeof c.daId !== "number") return false;
  if (typeof c.subsystem !== "string") return false;
  if (typeof c.commandName !== "string" || !c.commandName) return false;

  const status = String(c.catalogMatchStatus || "").toLowerCase();
  if (status !== "matched" && status !== "mismatched" && status !== "unknown") return false;

  if (c.validations !== undefined && typeof c.validations !== "object") return false;
  if (c.tmPayloadSchema !== undefined && typeof c.tmPayloadSchema !== "object") return false;

  return true;
};

export interface ValidationConfigApiResponse {
  status?: number;
  message?: string;
  recordsCount?: number;
  data: ValidationConfig[];
}

const isValidApiResponse = (response: unknown): response is ValidationConfigApiResponse => {
  if (!response || typeof response !== "object") return false;
  const r = response as Record<string, unknown>;

  // Check that data exists and is an array
  if (!Array.isArray(r.data)) return false;

  return true;
};

// Parses the raw API response from getValidationConfigs.
// The API returns { status, message, recordsCount, data: ValidationConfig[] }.
// catalogMatchStatus is normalized here because the API may return any casing (e.g. "matched", "MATCHED").
export const parseValidationConfigResponse = (
  response: unknown
): { success: boolean; configs: ValidationConfig[]; error?: string } => {
  if (!isValidApiResponse(response)) {
    return { success: false, configs: [], error: "Invalid API response structure" };
  }

  const mappedData = response.data.map((config) => {
    if (config && typeof config === "object") {
      const c = config as any;
      const status = String(c.catalogMatchStatus || "").toLowerCase();
      let catalogMatchStatus: CatalogMatchStatus = "Unknown";
      if (status === "matched") catalogMatchStatus = "Matched";
      else if (status === "mismatched") catalogMatchStatus = "Mismatched";

      return {
        ...c,
        catalogMatchStatus,
      };
    }
    return config;
  });

  const validConfigs = mappedData.filter(isValidValidationConfig);

  if (validConfigs.length === 0 && response.data.length > 0) {
    return { success: false, configs: [], error: "No valid validation configs in response" };
  }

  if (validConfigs.length < response.data.length) {
    console.warn(
      `Validation Config API: ${response.data.length - validConfigs.length} invalid configs filtered out`
    );
  }

  return { success: true, configs: validConfigs };
};

